import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Button, Form, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Connector } from 'models/Connector';
import { useAuth } from 'hooks/useAuth';
import {
  getCarrierConnectionConfig,
  getCarrierConnectionDefaults,
  getCarrierRequiredFields,
  getCarrierUserInstruction,
  getCarrierDisplayName,
  formatConnectionFieldLabel,
} from 'services/carrierService';

type ConnectorEditProps = {
  connector: Connector;
  onSave: (connector: Connector) => void;
  onCancel: () => void;
  handleUpdate: (id: number, updatedConnector: Connector) => Promise<void>;
};

const carrierLogos = (require as any).context('./carrier-logos', false, /\.(png|jpe?g|svg)$/);

const getCarrierLogo = (carrier?: string) => {
  if (!carrier) return null;
  const normalized = carrier.trim().toLowerCase();
  if (!normalized) return null;
  try {
    return carrierLogos(`./${normalized}.png`);
  } catch {
    return null;
  }
};

const ConnectorEditForm: React.FC<ConnectorEditProps> = ({
  connector,
  onSave,
  onCancel,
  handleUpdate,
}) => {
  const { accessToken } = useAuth();

  const [formData, setFormData] = useState<Connector>({ ...connector });
  const [carrierConfigs, setCarrierConfigs] = useState<any[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});

  const togglePasswordVisibility = (fieldName: string) => {
    setPasswordVisibility((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // وقتی connector عوض بشه
  useEffect(() => {
    setFormData({ ...connector });
  }, [connector]);

  // گرفتن تنظیمات carrier از سرور
  useEffect(() => {
    if (!accessToken) return;
    let ignore = false;

    const loadConfigs = async () => {
      setLoadingConfig(true);
      setConfigError(null);
      try {
        const data = await getCarrierConnectionConfig(accessToken);
        if (!ignore) setCarrierConfigs(data);
      } catch (err: any) {
        if (!ignore) setConfigError(err?.message ?? 'Failed to load carrier config');
      } finally {
        if (!ignore) setLoadingConfig(false);
      }
    };

    loadConfigs();
    return () => { ignore = true; };
  }, [accessToken]);

  // فیلدهای موردنیاز برای carrier فعلی
  const requiredFields = useMemo(() => {
    if (!formData?.carrier || !carrierConfigs.length) return [];
    return getCarrierRequiredFields(formData.carrier, carrierConfigs);
  }, [formData.carrier, carrierConfigs]);

  const connectionDefaults = useMemo(
    () =>
      formData?.carrier
        ? getCarrierConnectionDefaults(formData.carrier, carrierConfigs)
        : {},
    [formData?.carrier, carrierConfigs],
  );

  const carrierInstructionText = useMemo(() => {
    if (!formData?.carrier) return '';
    return getCarrierUserInstruction(formData.carrier, carrierConfigs);
  }, [formData?.carrier, carrierConfigs]);

  const carrierLogoSrc = useMemo(
    () => getCarrierLogo(formData?.carrier),
    [formData?.carrier]
  );

  const carrierDisplayName = useMemo(
    () => getCarrierDisplayName(formData?.carrier ?? '', carrierConfigs),
    [formData?.carrier, carrierConfigs],
  );

  // تغییر مقدار فیلدها
  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, type, value, checked } = e.target;
  
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  

  // ذخیره تغییرات
  const handleSubmit = async () => {
    try {
      await handleUpdate(formData.id, formData);
      onSave(formData);
    } catch (err) {
      console.error('Error updating connector:', err);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loadingConfig) return;
    void handleSubmit();
  };

  return (
    <Modal show onHide={onCancel} centered>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Connector</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingConfig ? (
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {configError && <div className="text-danger mb-3">{configError}</div>}
            {carrierLogoSrc && (
              <div className="mb-2 d-flex justify-content-center">
                <img
                  src={carrierLogoSrc}
                  alt={`${carrierDisplayName} logo`}
                  style={{ width: 80, height: 80, objectFit: 'contain' }}
                />
              </div>
            )}
            {carrierInstructionText && (
              <div className="text-700 mb-3">
                {carrierInstructionText}
              </div>
            )}

            {/* Carrier - غیرفعال */}
            <Form.Group controlId="formCarrier" className="mb-3">
              <Form.Label>Carrier</Form.Label>
              <Form.Control
                type="text"
                name="carrier"
                value={carrierDisplayName}
                disabled
              />
            </Form.Group>

            {/* Username - غیرفعال */}
            <Form.Group controlId="formUsername" className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                disabled
              />
            </Form.Group>

            {/* فیلدهای داینامیک براساس carrier */}
            {requiredFields.map((fieldName: string) => {
              const isPassword = fieldName.toLowerCase().includes('password');
              const showPassword = passwordVisibility[fieldName];
              const defaultForField = connectionDefaults[fieldName];
              const hasDefault = defaultForField !== undefined;
              const labelText = formatConnectionFieldLabel(fieldName);
              const placeholder = hasDefault
                ? defaultForField
                : `Enter ${fieldName.replace(/_/g, ' ')}`;
              return (
                <Form.Group key={fieldName} controlId={`form-${fieldName}`} className="mb-3">
                  <Form.Label>
                    {hasDefault ? `${labelText} (optional)` : labelText}
                  </Form.Label>
                  {isPassword ? (
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name={fieldName}
                        value={(formData as any)[fieldName] ?? ''}
                        onChange={handleChange}
                        placeholder={placeholder}
                      />
                      <Button
                        variant="outline-primary"
                        onClick={() => togglePasswordVisibility(fieldName)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Control
                      type="text"
                      name={fieldName}
                      value={(formData as any)[fieldName] ?? ''}
                      onChange={handleChange}
                      placeholder={placeholder}
                    />
                  )}
                </Form.Group>
              );
            })}

            {/* Enabled - toggle */}
            <Form.Group controlId="formEnabled" className="mb-3">
              <Form.Check
                type="switch"
                id="formEnabledSwitch"
                name="enabled"
                label="Enabled"
                checked={formData.enabled}
                onChange={handleChange}
              />
            </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loadingConfig}>
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ConnectorEditForm;
